import { useRef } from 'react';
import type React from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { DataTableSearchConfig } from '../types';

export interface TableToolbarSearchProps {
  value: string;
  onChange: (value: string) => void;
  searchConfig?: DataTableSearchConfig;
}

export function TableToolbarSearch({
  value,
  onChange,
  searchConfig,
}: TableToolbarSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  if (!searchConfig) {
    return null;
  }

  const inputProps = searchConfig.inputProps ?? {};
  const inputAriaLabel =
    inputProps['aria-label'] ?? searchConfig.placeholder ?? 'Search table';

  const mergedInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...inputProps,
    value,
    onChange: event => onChange(event.target.value),
    'aria-label': inputAriaLabel,
  };

  const handleClear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative flex items-center">
      <Search className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground" />
      <Input
        ref={inputRef}
        {...mergedInputProps}
        placeholder={searchConfig.placeholder ?? 'Search'}
        className={cn(
          'h-8 w-[220px] pl-7 pr-8 text-xs',
          searchConfig.className
        )}
      />
      {value && (
        <Button
          type="button"
          variant="ghost"
          onClick={handleClear}
          className="absolute right-2 h-4 w-4 p-0 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-transparent transition-colors"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
